/**
 * EinstellungenTab – Multi-Konto E-Mail IMAP/SMTP + Dashboard-Anzeige + Fristen-Erinnerungen
 */
import { useState, useEffect, useCallback } from 'react';
import { Mail, Eye, EyeOff, RefreshCw, Check, Trash2, Plug, Download, Plus, ChevronDown, ChevronUp, Power, Bot, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import FristenReminderSettings from './FristenReminderSettings';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const EMPTY_ACCOUNT = {
  name: '', imap_server: '', imap_port: 993, imap_use_ssl: true,
  smtp_server: '', smtp_port: 587, smtp_use_tls: true,
  email_address: '', username: '', password: '',
  auto_fetch: true, auto_analyse: true, auto_aufgaben: true, auto_entwurf: true,
};

export default function EinstellungenTab() {
  const [edvUmzugVisible, setEdvUmzugVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ ...EMPTY_ACCOUNT });
  const [saving, setSaving] = useState(false);
  const [expandedAccount, setExpandedAccount] = useState(null);
  const [editForms, setEditForms] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [testingId, setTestingId] = useState(null);
  const [fetchingId, setFetchingId] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [settingsRes, accountsRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/edv-umzug/settings`),
        fetch(`${BACKEND_URL}/api/email/accounts`),
        fetch(`${BACKEND_URL}/api/email/stats`),
      ]);
      const settingsData = await settingsRes.json();
      setEdvUmzugVisible(settingsData.visible_on_dashboard !== false);

      const accountsData = await accountsRes.json();
      setAccounts(Array.isArray(accountsData) ? accountsData : []);

      const statsData = await statsRes.json();
      if (statsData.success) setEmailStats(statsData);
    } catch (e) {
      console.error('Fehler beim Laden:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleEdvUmzug = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/edv-umzug/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visible_on_dashboard: !edvUmzugVisible })
      });
      setEdvUmzugVisible(!edvUmzugVisible);
      toast.success(edvUmzugVisible ? 'EDV Umzug ausgeblendet' : 'EDV Umzug eingeblendet');
    } catch { toast.error('Fehler beim Speichern'); }
  };

  // === Konto erstellen ===
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!newForm.name || !newForm.imap_server || !newForm.email_address || !newForm.username || !newForm.password) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/email/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newForm),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Konto "${newForm.name}" angelegt`);
        setNewForm({ ...EMPTY_ACCOUNT });
        setShowNewForm(false);
        loadData();
      } else {
        toast.error(data.error || 'Fehler beim Anlegen');
      }
    } catch { toast.error('Fehler'); }
    finally { setSaving(false); }
  };

  // === Konto aktualisieren ===
  const handleUpdateAccount = async (accountId) => {
    const form = editForms[accountId];
    if (!form) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      const res = await fetch(`${BACKEND_URL}/api/email/accounts/${accountId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Konto aktualisiert');
        loadData();
      } else toast.error(data.error || 'Fehler');
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  // === Konto löschen ===
  const handleDeleteAccount = async (accountId, name) => {
    if (!window.confirm(`E-Mail-Konto "${name}" wirklich löschen?`)) return;
    try {
      await fetch(`${BACKEND_URL}/api/email/accounts/${accountId}`, { method: 'DELETE' });
      toast.success('Konto gelöscht');
      loadData();
    } catch { toast.error('Fehler'); }
  };

  // === Verbindung testen ===
  const handleTestConnection = async (accountId) => {
    setTestingId(accountId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/email/accounts/${accountId}/test`, { method: 'POST' });
      const data = await res.json();
      data.success ? toast.success(data.message) : toast.error(data.error);
    } catch { toast.error('Test fehlgeschlagen'); }
    finally { setTestingId(null); }
  };

  // === Jetzt abrufen ===
  const handleFetchNow = async (accountId) => {
    setFetchingId(accountId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/email/accounts/${accountId}/fetch`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || `${data.new_emails} neue E-Mails`);
        loadData();
      } else toast.error(data.error);
    } catch { toast.error('Abruf fehlgeschlagen'); }
    finally { setFetchingId(null); }
  };

  // === Konto aktivieren/deaktivieren ===
  const handleToggleActive = async (accountId, currentState) => {
    try {
      await fetch(`${BACKEND_URL}/api/email/accounts/${accountId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aktiv: !currentState }),
      });
      loadData();
    } catch { toast.error('Fehler'); }
  };

  // === Konto expandieren / Edit-Form init ===
  const toggleExpand = (acc) => {
    if (expandedAccount === acc.id) {
      setExpandedAccount(null);
    } else {
      setExpandedAccount(acc.id);
      if (!editForms[acc.id]) {
        setEditForms(prev => ({
          ...prev,
          [acc.id]: {
            name: acc.name || '',
            imap_server: acc.imap_server || '',
            imap_port: acc.imap_port || 993,
            smtp_server: acc.smtp_server || '',
            smtp_port: acc.smtp_port || 587,
            email_address: acc.email_address || '',
            username: acc.username || '',
            password: '',
            auto_fetch: acc.auto_fetch ?? true,
            auto_analyse: acc.auto_analyse ?? true,
            auto_aufgaben: acc.auto_aufgaben ?? true,
            auto_entwurf: acc.auto_entwurf ?? true,
          }
        }));
      }
    }
  };

  const updateEditForm = (accountId, field, value) => {
    setEditForms(prev => ({
      ...prev,
      [accountId]: { ...prev[accountId], [field]: value }
    }));
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Lädt...</div>;

  return (
    <div className="space-y-6">
      {/* Dashboard-Anzeige */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Dashboard-Anzeige</h3>
        <div className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Download className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="font-medium text-slate-800">EDV Umzug Kachel</p>
              <p className="text-sm text-slate-500">
                Datenmigrations-Tool auf dem Dashboard {edvUmzugVisible ? 'anzeigen' : 'ausblenden'}
              </p>
            </div>
          </div>
          <Button variant={edvUmzugVisible ? 'default' : 'outline'} onClick={toggleEdvUmzug}>
            {edvUmzugVisible ? <><Eye className="w-4 h-4 mr-2" />Sichtbar</> : <><EyeOff className="w-4 h-4 mr-2" />Ausgeblendet</>}
          </Button>
        </div>
      </div>

      {/* E-Mail-Konten */}
      <div className="bg-white rounded-lg border border-slate-200 p-6" data-testid="email-accounts-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              E-Mail-Konten (IMAP/SMTP)
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Automatischer Abruf alle 30 Minuten &middot; Claude analysiert, erstellt Aufgaben & Antwortentwürfe
            </p>
          </div>
          <div className="flex items-center gap-2">
            {emailStats && (
              <span className="text-xs text-slate-500">
                {emailStats.total_emails} E-Mails &middot; {emailStats.aufgaben_erstellt || 0} Aufgaben &middot; {emailStats.accounts_aktiv || 0} Konten aktiv
              </span>
            )}
            <Button size="sm" onClick={() => setShowNewForm(!showNewForm)} data-testid="email-add-account-btn">
              <Plus className="w-4 h-4 mr-1" /> Konto hinzufügen
            </Button>
          </div>
        </div>

        {/* Neues Konto Formular */}
        {showNewForm && (
          <div className="mb-4 p-4 border-2 border-dashed border-blue-200 rounded-lg bg-blue-50/30">
            <h4 className="font-medium text-sm mb-3">Neues E-Mail-Konto</h4>
            <form onSubmit={handleCreateAccount} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Kontoname *</Label>
                  <Input value={newForm.name} onChange={e => setNewForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="z.B. Büro Hauptkonto" className="h-8 text-xs" data-testid="new-email-name" />
                </div>
                <div>
                  <Label className="text-xs">E-Mail-Adresse *</Label>
                  <Input value={newForm.email_address} onChange={e => setNewForm(p => ({ ...p, email_address: e.target.value }))}
                    placeholder="buero@procura-bayern.de" className="h-8 text-xs" data-testid="new-email-address" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">IMAP Server *</Label>
                  <Input value={newForm.imap_server} onChange={e => setNewForm(p => ({ ...p, imap_server: e.target.value }))}
                    placeholder="imap.provider.de" className="h-8 text-xs" data-testid="new-imap-server" />
                </div>
                <div>
                  <Label className="text-xs">IMAP Port</Label>
                  <Input type="number" value={newForm.imap_port} onChange={e => setNewForm(p => ({ ...p, imap_port: parseInt(e.target.value) || 993 }))}
                    className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-xs">SMTP Port</Label>
                  <Input type="number" value={newForm.smtp_port} onChange={e => setNewForm(p => ({ ...p, smtp_port: parseInt(e.target.value) || 587 }))}
                    className="h-8 text-xs" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">SMTP Server</Label>
                  <Input value={newForm.smtp_server} onChange={e => setNewForm(p => ({ ...p, smtp_server: e.target.value }))}
                    placeholder="smtp.provider.de" className="h-8 text-xs" data-testid="new-smtp-server" />
                </div>
                <div>
                  <Label className="text-xs">Benutzername *</Label>
                  <Input value={newForm.username} onChange={e => setNewForm(p => ({ ...p, username: e.target.value }))}
                    placeholder="buero@..." className="h-8 text-xs" data-testid="new-email-username" />
                </div>
                <div>
                  <Label className="text-xs">Passwort *</Label>
                  <Input type="password" value={newForm.password} onChange={e => setNewForm(p => ({ ...p, password: e.target.value }))}
                    className="h-8 text-xs" data-testid="new-email-password" />
                </div>
              </div>
              <div className="flex items-center gap-6 p-2 bg-white rounded border text-xs">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={newForm.auto_fetch} onChange={e => setNewForm(p => ({ ...p, auto_fetch: e.target.checked }))} className="rounded" />
                  Auto-Abruf
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={newForm.auto_analyse} onChange={e => setNewForm(p => ({ ...p, auto_analyse: e.target.checked }))} className="rounded" />
                  <Bot className="w-3 h-3" /> KI-Analyse
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={newForm.auto_aufgaben} onChange={e => setNewForm(p => ({ ...p, auto_aufgaben: e.target.checked }))} className="rounded" />
                  Aufgaben erstellen
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={newForm.auto_entwurf} onChange={e => setNewForm(p => ({ ...p, auto_entwurf: e.target.checked }))} className="rounded" />
                  <FileText className="w-3 h-3" /> Antwortentwurf
                </label>
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={saving} data-testid="new-email-save-btn">
                  {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                  Konto anlegen
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewForm(false)}>Abbrechen</Button>
              </div>
            </form>
          </div>
        )}

        {/* Kontoliste */}
        {accounts.length === 0 && !showNewForm ? (
          <div className="text-center py-8 text-sm text-slate-400">
            <Mail className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>Noch keine E-Mail-Konten konfiguriert</p>
            <p className="text-xs mt-1">Fügen Sie ein Konto hinzu, um den automatischen E-Mail-Abruf zu starten</p>
          </div>
        ) : (
          <div className="space-y-2">
            {accounts.map(acc => (
              <div key={acc.id} className={`border rounded-lg transition-colors ${acc.aktiv !== false ? 'border-slate-200' : 'border-slate-100 bg-slate-50 opacity-60'}`}
                data-testid={`email-account-${acc.id}`}>
                {/* Header */}
                <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50" onClick={() => toggleExpand(acc)}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${acc.aktiv !== false ? 'bg-blue-100' : 'bg-slate-200'}`}>
                      <Mail className={`w-4 h-4 ${acc.aktiv !== false ? 'text-blue-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{acc.name || acc.email_address}</p>
                      <p className="text-xs text-slate-500">{acc.email_address} &middot; {acc.imap_server}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {acc.last_fetch && (
                      <span className="text-[10px] text-slate-400">
                        Letzter Abruf: {new Date(acc.last_fetch).toLocaleString('de-DE', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                        {acc.last_fetch_count > 0 && ` (${acc.last_fetch_count} neu)`}
                      </span>
                    )}
                    {acc.auto_analyse && <Badge variant="outline" className="text-[10px] py-0"><Bot className="w-2.5 h-2.5 mr-0.5" />KI</Badge>}
                    {acc.auto_aufgaben && <Badge variant="outline" className="text-[10px] py-0">Aufgaben</Badge>}
                    <Badge variant={acc.aktiv !== false ? 'default' : 'secondary'} className="text-[10px] py-0">
                      {acc.aktiv !== false ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                    {expandedAccount === acc.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedAccount === acc.id && editForms[acc.id] && (
                  <div className="px-4 pb-4 border-t border-slate-100">
                    <div className="grid grid-cols-4 gap-3 mt-3">
                      <div>
                        <Label className="text-xs">Kontoname</Label>
                        <Input value={editForms[acc.id].name} onChange={e => updateEditForm(acc.id, 'name', e.target.value)}
                          className="h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-xs">E-Mail-Adresse</Label>
                        <Input value={editForms[acc.id].email_address} onChange={e => updateEditForm(acc.id, 'email_address', e.target.value)}
                          className="h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-xs">Benutzername</Label>
                        <Input value={editForms[acc.id].username} onChange={e => updateEditForm(acc.id, 'username', e.target.value)}
                          className="h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-xs">Neues Passwort (leer = unverändert)</Label>
                        <div className="relative">
                          <Input type={showPasswords[acc.id] ? 'text' : 'password'}
                            value={editForms[acc.id].password} onChange={e => updateEditForm(acc.id, 'password', e.target.value)}
                            placeholder="••••••••" className="h-8 text-xs pr-8" />
                          <button type="button" className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-600"
                            onClick={() => setShowPasswords(p => ({ ...p, [acc.id]: !p[acc.id] }))}>
                            {showPasswords[acc.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-3 mt-2">
                      <div>
                        <Label className="text-xs">IMAP Server</Label>
                        <Input value={editForms[acc.id].imap_server} onChange={e => updateEditForm(acc.id, 'imap_server', e.target.value)}
                          className="h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-xs">IMAP Port</Label>
                        <Input type="number" value={editForms[acc.id].imap_port} onChange={e => updateEditForm(acc.id, 'imap_port', parseInt(e.target.value) || 993)}
                          className="h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-xs">SMTP Server</Label>
                        <Input value={editForms[acc.id].smtp_server} onChange={e => updateEditForm(acc.id, 'smtp_server', e.target.value)}
                          className="h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-xs">SMTP Port</Label>
                        <Input type="number" value={editForms[acc.id].smtp_port} onChange={e => updateEditForm(acc.id, 'smtp_port', parseInt(e.target.value) || 587)}
                          className="h-8 text-xs" />
                      </div>
                    </div>
                    <div className="flex items-center gap-6 mt-3 p-2 bg-slate-50 rounded border text-xs">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={editForms[acc.id].auto_fetch}
                          onChange={e => updateEditForm(acc.id, 'auto_fetch', e.target.checked)} className="rounded" />
                        Auto-Abruf (30 Min)
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={editForms[acc.id].auto_analyse}
                          onChange={e => updateEditForm(acc.id, 'auto_analyse', e.target.checked)} className="rounded" />
                        <Bot className="w-3 h-3" /> KI-Analyse
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={editForms[acc.id].auto_aufgaben}
                          onChange={e => updateEditForm(acc.id, 'auto_aufgaben', e.target.checked)} className="rounded" />
                        Aufgaben erkennen
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={editForms[acc.id].auto_entwurf}
                          onChange={e => updateEditForm(acc.id, 'auto_entwurf', e.target.checked)} className="rounded" />
                        <FileText className="w-3 h-3" /> Antwortentwurf
                      </label>
                    </div>
                    <Separator className="my-3" />
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={() => handleUpdateAccount(acc.id)} disabled={saving} data-testid={`email-save-${acc.id}`}>
                        {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                        Speichern
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleTestConnection(acc.id)} disabled={testingId === acc.id}>
                        {testingId === acc.id ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plug className="w-3.5 h-3.5 mr-1" />}
                        Verbindung testen
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleFetchNow(acc.id)} disabled={fetchingId === acc.id}>
                        {fetchingId === acc.id ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                        Jetzt abrufen
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleToggleActive(acc.id, acc.aktiv !== false)}>
                        <Power className="w-3.5 h-3.5 mr-1" />
                        {acc.aktiv !== false ? 'Deaktivieren' : 'Aktivieren'}
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteAccount(acc.id, acc.name)}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Löschen
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fristen-Erinnerungen */}
      <div className="bg-white rounded-lg border p-4">
        <FristenReminderSettings />
      </div>
    </div>
  );
}
